import type { StateCreator } from 'zustand';
import type {
  VaultItem,
  VaultStats,
  VaultFilters,
  VaultQueueStatus,
  TensionCategory,
  VaultAnalysisSSEEvent,
  VaultQueueSSEEvent,
} from '../types';
import { vaultApi } from '../services/vaultApi';

export interface VaultSlice {
  vaultItems: VaultItem[];
  vaultTotal: number;
  vaultStats: VaultStats | null;
  vaultSelectedItemId: number | null;
  vaultFilters: VaultFilters;
  vaultLoading: boolean;
  vaultQueue: VaultQueueStatus;
  vaultTensions: TensionCategory[];
  vaultBatchProgress: { total: number; completed: number } | null;

  fetchVaultItems: () => Promise<void>;
  addToVault: (postId: number, strategyId?: number) => Promise<{ item_id?: number; existing?: boolean }>;
  addToVaultByUrl: (url: string, strategyId?: number) => Promise<{ item_id?: number; existing?: boolean; needs_scrape?: boolean; url?: string }>;
  scrapeAndAddToVault: (url: string, strategyId?: number) => Promise<{ item_id: number }>;
  updateVaultItem: (itemId: number, updates: Partial<VaultItem>) => Promise<void>;
  deleteVaultItem: (itemId: number) => Promise<void>;
  reanalyzeVaultItem: (itemId: number) => Promise<void>;
  selectVaultItem: (itemId: number | null) => void;
  setVaultFilters: (partial: Partial<VaultFilters>) => void;
  fetchVaultStats: () => Promise<void>;
  fetchTensions: () => Promise<void>;
  handleVaultSSE: (event: VaultAnalysisSSEEvent | VaultQueueSSEEvent) => void;
}

const DEFAULT_FILTERS: VaultFilters = {
  sort: 'newest',
  limit: 50,
  offset: 0,
};

export const createVaultSlice: StateCreator<VaultSlice, [], [], VaultSlice> = (set, get) => ({
  vaultItems: [],
  vaultTotal: 0,
  vaultStats: null,
  vaultSelectedItemId: null,
  vaultFilters: DEFAULT_FILTERS,
  vaultLoading: false,
  vaultQueue: { queue_size: 0, analyzing_item_id: null },
  vaultTensions: [],
  vaultBatchProgress: null,

  fetchVaultItems: async () => {
    set({ vaultLoading: true });
    try {
      const result = await vaultApi.listItems(get().vaultFilters);
      set({ vaultItems: result.items, vaultTotal: result.total });
    } finally {
      set({ vaultLoading: false });
    }
  },

  addToVault: async (postId, strategyId) => {
    const result = await vaultApi.addByPostId(postId, strategyId);
    if (!result.existing) {
      get().fetchVaultItems();
    }
    return result;
  },

  addToVaultByUrl: async (url, strategyId) => {
    return vaultApi.addByUrl(url, strategyId);
  },

  scrapeAndAddToVault: async (url, strategyId) => {
    const result = await vaultApi.scrapeAndAdd(url, strategyId);
    get().fetchVaultItems();
    return result;
  },

  updateVaultItem: async (itemId, updates) => {
    const updated = await vaultApi.updateItem(itemId, updates);
    set((s) => ({
      vaultItems: s.vaultItems.map((i) => (i.id === itemId ? updated : i)),
    }));
  },

  deleteVaultItem: async (itemId) => {
    await vaultApi.deleteItem(itemId);
    set((s) => ({
      vaultItems: s.vaultItems.filter((i) => i.id !== itemId),
      vaultTotal: s.vaultTotal - 1,
      vaultSelectedItemId: s.vaultSelectedItemId === itemId ? null : s.vaultSelectedItemId,
    }));
  },

  reanalyzeVaultItem: async (itemId) => {
    await vaultApi.reanalyze(itemId);
    set((s) => ({
      vaultItems: s.vaultItems.map((i) =>
        i.id === itemId ? { ...i, analysis_status: 'queued' as const } : i
      ),
    }));
  },

  selectVaultItem: (itemId) => set({ vaultSelectedItemId: itemId }),

  setVaultFilters: (partial) => {
    set((s) => ({
      vaultFilters: { ...s.vaultFilters, ...partial, offset: 0 },
    }));
    get().fetchVaultItems();
  },

  fetchVaultStats: async () => {
    const stats = await vaultApi.getStats();
    set({ vaultStats: stats });
  },

  fetchTensions: async () => {
    const tensions = await vaultApi.getTensions();
    set({ vaultTensions: tensions });
  },

  handleVaultSSE: (event) => {
    if (event.event === 'vault_analysis_complete') {
      const evt = event as VaultAnalysisSSEEvent;
      set((s) => {
        const updatedItems = s.vaultItems.map((item) => {
          if (item.id === evt.item_id) {
            return { ...item, analysis_status: evt.status };
          }
          return item;
        });

        const completed = (s.vaultBatchProgress?.completed ?? 0) + 1;
        const total = s.vaultBatchProgress?.total ?? completed;
        const newProgress = total > 1 ? { total, completed } : null;

        return { vaultItems: updatedItems, vaultBatchProgress: newProgress };
      });

      // Reload the updated item to get full analysis
      if (evt.status === 'succeeded') {
        vaultApi.getItem(evt.item_id).then((updated) => {
          set((s) => ({
            vaultItems: s.vaultItems.map((i) => (i.id === evt.item_id ? updated : i)),
          }));
        }).catch(() => {});
      }
    } else if (event.event === 'vault_queue_update') {
      const evt = event as VaultQueueSSEEvent;
      set((s) => {
        const queue: VaultQueueStatus = {
          queue_size: evt.queue_size,
          analyzing_item_id: evt.analyzing_item_id ?? null,
        };

        // Initialize batch progress when queue grows
        const prevQueue = s.vaultQueue.queue_size;
        let batchProgress = s.vaultBatchProgress;
        if (evt.queue_size > 0 && prevQueue === 0) {
          batchProgress = { total: evt.queue_size, completed: 0 };
        } else if (evt.queue_size === 0) {
          batchProgress = null;
        }

        return { vaultQueue: queue, vaultBatchProgress: batchProgress };
      });
    }
  },
});
